import config from 'config'
import { PAGE_ACTION_TYPES } from '~/app/constants'
import { instrumenter } from '~/app/utils/instrumentation'
import type { DesignPersonalizationContext } from '@vp/personalization-types'

import { AbstractService, LoggerParameter } from 'shared/services/AbstractService'
import { TEMPLATE_PURPOSES, SSR_CONTENT_QUERY_TIMEOUT } from '~/shared/constants'
import getLogger from 'shared/logger'
import { IS_BROWSER } from '~/app/constants'
import { createApiClient } from 'shared/services/ApiClient'
import { stringifyArrayFormatRepeat } from '~/shared/url'
/* eslint-disable @typescript-eslint/no-explicit-any */
//TODO MIGRATION T011: Fix eslint errors

export type ContentQuery = {
  galleryName: string
  productVersion: number
  galleryType: State.GalleryType
  productKey: string
  bypassApproval: Gallery.Models.Url.ValidParsedQsValue<boolean>
  locale: i18n.Locale
  mpvId: Gallery.Models.Url.ValidParsedQsValue<string>
  offset?: number
  length?: number
  facetTaxonomyIds?: string[]
  attributes?: string[][]
  categories?: string[][]
  facetCategories?: string[]
  keyword?: string[]
  collection?: string
  selectedOptions?: Record<string, any>
  designIds?: string[]
  // Note that currently accessing a DPC in content causes issues with favorites
  // As we save the diamond preview urls to WES
  // Do not enable until this is solved somehow
  designPersonalizationContext?: DesignPersonalizationContext
  designPersonalizationContextId?: string
  noCache: Gallery.Models.Url.ValidParsedQsValue<boolean>
  useConstraints: Gallery.Models.Url.ValidParsedQsValue<boolean>
  forcedRankingStrategyOverride: Gallery.Models.Url.ValidParsedQsValue<string>
  renderProps: Record<string, string | null | undefined>
  templatePurposes?: TEMPLATE_PURPOSES[]
  searchBackend?: Gallery.Models.Url.ValidParsedQsValue<string>
  experimentFlags: Record<string, string>
  designCreationTypes?: Gallery.Models.Url.ValidParsedQsValue<string[]>
  debug?: boolean
  includeCategoryAndKeywordData?: boolean
  boostedSearchableEnumeratedData?: Gallery.ContentQuery.BoostedSearchableEnumeratedData[]
  filterAltTextCompatibleTagging?: boolean
  moreLike?: string[]
  sets?: Gallery.Models.Url.ValidParsedQsValue<Gallery.Models.Url.JSONObject[]>
}

const DEFAULT_CONTENT_QUERY = {} as ContentQuery

export function calculateOffset(page: number, pageSize: number): number {
  return (page - 1) * pageSize
}

const paramsSerializer = (complexObjectKey: string) => {
  return (params: any) => {
    const complexValue = params[complexObjectKey]

    const { [complexObjectKey]: _, ...otherParams } = params

    let queryString = stringifyArrayFormatRepeat(otherParams)

    // Manually append complex object parameters to avoid qs.stringify issues
    if (Array.isArray(complexValue) && complexValue.length > 0) {
      complexValue.forEach((item: any) => {
        queryString += `&${complexObjectKey}=${encodeURIComponent(JSON.stringify(item))}`
      })
    }

    return queryString
  }
}

const formatEnumeratedBoostParams = (boosts: Array<{ key: string; value: string; weight: number }> | undefined): string =>
  boosts?.map((boost) => `${boost.key}:${boost.value}:${boost.weight.toFixed(2)}`).join(',') ?? ''

export class ContentQueryApi extends AbstractService implements Services.IContentService {
  constructor(serviceConfig: Gallery.Config.IServiceConfig, logger: LoggerParameter) {
    super(serviceConfig, logger)

    if (!IS_BROWSER) {
      this.api = createApiClient(
        {
          ...serviceConfig,
          timeout: SSR_CONTENT_QUERY_TIMEOUT,
        },
        this.constructor.name,
      )
    }
  }

  /**
   * Given a ContentQuery object, request new content from the ContentService
   *
   * @param query
   * @throws ServiceError
   */
  public async getContent(query: ContentQuery = DEFAULT_CONTENT_QUERY): Promise<Gallery.ContentQuery.ContentResponseWithMetadata> {
    const {
      galleryType,
      productKey,
      attributes = [],
      facetTaxonomyIds = [],
      bypassApproval,
      categories = [],
      locale,
      facetCategories,
      galleryName,
      productVersion,
      keyword,
      collection,
      length,
      offset,
      selectedOptions,
      noCache,
      useConstraints,
      forcedRankingStrategyOverride,
      designIds,
      renderProps,
      templatePurposes,
      searchBackend,
      experimentFlags,
      designCreationTypes,
      debug,
      includeCategoryAndKeywordData,
      sets,
      boostedSearchableEnumeratedData = [],
      mpvId,
      ...rest
    } = query

    if (!galleryName) {
      throw new Error('Parameter "galleryName" must be provided')
    }

    const attributesFlat = attributes.map((attributeItem) => attributeItem.join(','))
    const isStandardGalleryType = galleryType === 'Standard'

    const url = isStandardGalleryType
      ? `v2/Galleries/${encodeURIComponent(galleryName)}/Culture/${locale}/Content`
      : `v2/${productKey}/Culture/${locale}/Content`

    const params = {
      productVersion,

      bypassApproval,
      categories: categories.map((categoryItem) => categoryItem.join(',')),
      facetCategories,
      keywords: [keyword],
      collection,
      limit: length,
      offset,
      requestor: noCache ? config.noCacheRequestor : config.appName,
      noCache,
      useConstraints,
      forcedRankingStrategyOverride,
      selectedOptions,
      designIds: designIds?.join(','),
      renderProps,
      templatePurposes,
      searchBackend,
      experimentFlags,
      designCreationTypes,
      debug,
      includeCategoryAndKeywordData,
      ...(boostedSearchableEnumeratedData?.length && {
        boostedSearchableEnumeratedData: formatEnumeratedBoostParams(boostedSearchableEnumeratedData),
      }),
      sets,
      attributes: attributesFlat,
      ...(!isStandardGalleryType && { facetTaxonomyIds }),
      ...(isStandardGalleryType && { mpvId }),
      ...rest,
    }

    try {
      const { data, status } = await this.api.get<Gallery.ContentQuery.Response>(url, {
        params,
        paramsSerializer: paramsSerializer('sets'),
      })

      return {
        data,
        raw: JSON.stringify(data),
        meta: {
          serviceName: 'Content Service',
          baseUrl: config.services.designConductorService.prefixUrl,
          url,
          status,
          params,
        },
      }
    } catch (e) {
      instrumenter.recordPageAction(PAGE_ACTION_TYPES.CONTENT_LOAD_FAILED, {
        galleryName,
        info: e,
      })

      throw e
    }
  }

  /**
   * Retrieves matching templates for a specific product and template identifier
   *
   * @param query - The matching templates query parameters
   * @returns Promise with array of matching templates
   * @throws ServiceError
   */
  public async getMatchingTemplatesForProduct(query: Gallery.ContentQuery.GetMatchingTemplatesQuery): Promise<Gallery.ContentQuery.MatchingResponse[]> {
    const { productKey, productVersion, culture, templateIdentifier } = query

    if (!productKey) {
      throw new Error('Parameter "productKey" must be provided')
    }
    if (!templateIdentifier) {
      throw new Error('Parameter "templateIdentifier" must be provided')
    }

    const url = `v2/Product/${productKey}/Culture/${culture}/matching`

    try {
      const { data } = await this.api.get<Gallery.ContentQuery.MatchingResponse[]>(url, {
        params: {
          productVersion,
          requestor: config.appName,
          templateIdentifier,
        },
        paramsSerializer: stringifyArrayFormatRepeat,
      })

      return data
    } catch {
      return []
    }
  }
}

export const ContentService = new ContentQueryApi(config.services.designConductorService, getLogger)
